(() => {
  if (window.PersonalizationGuard) return;
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const tokens = (value) => String(value || '').split(',').map(normalize).filter(Boolean);
  const matches = (trigger, values) => !tokens(trigger).length || tokens(trigger).some((value) => values.includes(value));
  const controls = (field, form) => Array.from(field.querySelectorAll('input, select, textarea'))
    .filter((input) => input.form === form || input.getAttribute('form') === form.id);

  function sync(root) {
    if (!window.ActiveVariantSelection.isActive(root)) return;
    const values = window.ActiveVariantSelection.values(root);
    root.querySelectorAll('.product__custom-input').forEach((field) => {
      if (field.matches('liner-color-picker')) return;
      const active = !field.hidden && matches(field.dataset.triggerVariant, values);
      field.style.display = active ? 'block' : 'none';
      field.querySelectorAll('input, select, textarea').forEach((input) => {
        if (!input.name?.startsWith('properties[')) return;
        input.disabled = !active;
        input.setCustomValidity('');
        if (input.type !== 'hidden') input.required = active && field.dataset.required === 'true';
        if (input.classList.contains('image-upload__agreement-record') || input.classList.contains('image-upload__agreement-text-record')) {
          const file = field.querySelector('input[type="file"]');
          input.disabled = !active || !file?.files?.length || !input.value;
        }
      });
    });
    // Color pickers own their material/preset/exclusion rules. Refresh before
    // inspecting form elements so stale/disabled choices cannot be submitted.
    root.dispatchEvent(new CustomEvent('liner-colors:refresh', { bubbles: true }));
  }

  function fileError(input) {
    const file = input.files?.[0];
    if (!file) return '';
    const accepted = tokens(input.getAttribute('accept'));
    const type = normalize(file.type);
    if (accepted.length && !accepted.some((rule) => rule.startsWith('.')
      ? normalize(file.name).endsWith(rule)
      : rule.endsWith('/*') ? type.startsWith(rule.slice(0, -1)) : type === rule)) {
      return 'Please select a supported image file.';
    }
    const max = Number(input.dataset.maxSizeMb) || 20;
    return file.size > max * 1024 * 1024 ? `The image must be ${max}MB or smaller.` : '';
  }

  function fail(form, field, message) {
    form.closest('product-form')?.handleErrorMessage(message);
    let error = field.querySelector('[data-personalization-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.personalizationError = '';
      error.className = 'caption product-form__error-message';
      error.setAttribute('role', 'alert');
      error.setAttribute('tabindex', '-1');
      field.append(error);
    }
    error.textContent = message;
    error.hidden = false;
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    error.focus();
    return false;
  }

  function validate(form) {
    const root = form.closest('product-info');
    if (!root) return form.reportValidity();
    sync(root);
    for (const field of root.querySelectorAll('.product__custom-input')) {
      const inputs = controls(field, form);
      if (!inputs.length && field.dataset.formId !== form.id) continue;
      field.querySelector('[data-personalization-error]')?.setAttribute('hidden', '');
      if (field.hidden || field.style.display === 'none') continue;
      const active = inputs.filter((input) => !input.disabled && !input.matches(':disabled'));
      if (field.dataset.required === 'true') {
        let satisfied;
        if (field.matches('liner-color-picker')) satisfied = Boolean(field.selected);
        else if (active.some((input) => input.type === 'radio')) satisfied = active.some((input) => input.checked);
        else satisfied = active.some((input) => input.type === 'file' ? input.files?.length : input.value?.trim());
        if (!satisfied) return fail(form, field, 'Please complete this required option before adding to cart.');
      }
      for (const input of active.filter((input) => input.type === 'file')) {
        const message = fileError(input);
        if (message) return fail(form, field, message);
        const blockId = input.id.replace('ImageUploadInput-', '');
        const agreement = document.getElementById(`ImageUploadAgreement-${blockId}`);
        const checkbox = agreement?.querySelector('input[type="checkbox"]');
        if (input.files?.length && agreement?.dataset.required === 'true' && !checkbox?.checked) {
          return fail(form, field, 'Please accept the image upload agreement before adding to cart.');
        }
        const accepted = input.files?.length && checkbox?.checked;
        const record = field.querySelector('.image-upload__agreement-record');
        const textRecord = field.querySelector('.image-upload__agreement-text-record');
        if (record) {
          record.disabled = !accepted;
          record.value = accepted ? record.value || `Accepted ${new Date().toISOString()}` : '';
        }
        if (textRecord) {
          textRecord.disabled = !accepted;
          textRecord.value = accepted ? document.getElementById(`ImageUploadAgreementText-${blockId}`)?.textContent.trim().replace(/\s+/g, ' ') || '' : '';
        }
      }
    }
    // Also honor native validation, including gift-card recipient controls.
    return form.reportValidity();
  }

  function cleanFormData(form, data) {
    // Disabled inactive controls are omitted by native FormData. Empty optional
    // files must also be omitted; never convert real files into strings/JSON.
    const inputs = Array.from(form.elements);
    for (const input of inputs) {
      if (input.type !== 'file' || !input.name.startsWith('properties[')) continue;
      if (input.disabled || input.matches(':disabled') || input.files?.length) continue;
      // Conditional upload blocks may share the same property name. An empty
      // inactive/optional field must never delete another block's actual file.
      const hasUpload = inputs.some((other) => other.name === input.name && other.type === 'file'
        && !other.disabled && !other.matches(':disabled') && other.files?.length);
      if (!hasUpload) data.delete(input.name);
      else {
        const entries = data.getAll(input.name).filter((entry) => typeof entry === 'string' || entry.name !== '' || entry.size !== 0);
        data.delete(input.name);
        entries.forEach((entry) => data.append(input.name, entry));
      }
    }
    return data;
  }

  window.PersonalizationGuard = { sync, validate, cleanFormData };
  document.addEventListener('submit', (event) => {
    if (!event.target.matches('product-form form')) return;
    if (!validate(event.target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('change', (event) => {
    const root = event.target.closest('product-info');
    if (root && event.target.closest('variant-selects, .preset-picker, .product__custom-input')) sync(root);
  });
  const initialize = (scope) => {
    if (scope.matches?.('product-info')) sync(scope);
    scope.querySelectorAll('product-info').forEach(sync);
  };
  document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('product-info:variant-updated', (event) => sync(event.target));
  document.addEventListener('product-info:loaded', (event) => queueMicrotask(() => initialize(event.target)));
})();